import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { subscribeVisibleClaims } from '../data/claimQueries';
import { tallyClaims, ownClaims, tallyMatrix, reconciles, money } from '../data/claimTally';
import { LC_ROSTER } from '../data/lcRoster';

// THE CLAIM TALLY - read-only counters (task list section 493; Bob 2026-09-28). See src\data\claimTally.js
// for what every number is. Three views, one component:
//   mode="self"    - every signed-in person: his OWN claims (Dashboard).
//   mode="matrix"  - Local Chairperson: every member in his local committee(s) of adjustment, grouped by
//                    office (the LC seat); General Chairperson: the same at his level - every seat of his
//                    committee. What a role can see is what the rules already let it read (claimQueries);
//                    this component never widens it. Each name opens that member's file (onOpenMember).
// Nothing here writes.

const C = { text: '#F0F4F8', dim: '#8AA4BF', line: '#2B5C8A', panel: '#13263A', accent: '#E6A023', win: '#27AE60', loss: '#C0392B' };
const seatLabel = (k) => { const r = LC_ROSTER.find((x) => x.scopeKey === k); return r ? r.label.replace(/\s+\(.*\)$/, '') : k; };

function Cell({ n, amount, note, color }) {
  return (
    <td style={{ padding: '6px 8px', textAlign: 'right', borderTop: '1px solid ' + C.line, whiteSpace: 'nowrap' }}>
      <div style={{ color: color || C.text, fontWeight: 700 }}>{n}</div>
      {amount !== undefined ? <div style={{ color: C.dim, fontSize: 11 }}>{money(amount)}</div> : null}
      {note ? <div style={{ color: C.accent, fontSize: 10 }}>{note}</div> : null}
    </td>
  );
}

function notes(t) {
  return {
    sub: t.submitted.noAmount ? 'no amount on ' + t.submitted.noAmount : '',
    won: [t.awarded.unknownOn ? 'amount unknown on ' + t.awarded.unknownOn : '', t.awarded.unsourcedOn ? '+' + money(t.awarded.unsourced) + ' unsourced' : ''].filter(Boolean).join('; '),
    lost: t.unsuccessful.noAmount ? 'no amount on ' + t.unsuccessful.noAmount : '',
  };
}

function Cells({ t }) {
  const x = notes(t);
  return (
    <>
      <Cell n={t.submitted.n} amount={t.submitted.amount} note={x.sub} />
      <Cell n={t.awarded.n} amount={t.awarded.amount} note={x.won} color={C.win} />
      <Cell n={t.unsuccessful.n} amount={t.unsuccessful.amount} note={x.lost} color={C.loss} />
      <Cell n={t.open.n} />
      <Cell n={t.board.n} />
    </>
  );
}

const HEAD = ['Submitted', 'Awarded', 'Unsuccessful', 'Open', 'At a Board'];
const th = { padding: '6px 8px', textAlign: 'right', color: C.dim, fontSize: 11, fontWeight: 600 };
const FOOT = 'Read-only, counted from the claims you can read. Submitted = every claim past draft, at the amount claimed. ' +
  'Awarded = allowed in full, in part, or settled, at money recovered with a document or pay record behind it; ' +
  'a figure with no document is shown apart as unsourced. Unsuccessful = denied, withdrawn or abandoned, at the amount claimed. ' +
  'Open = no outcome recorded yet - never counted as won or lost. Drafts are never counted.';

export default function ClaimTally({ user, mode = 'self', onOpenMember, onNavigate }) {
  const [claims, setClaims] = useState(null);
  const [folded, setFolded] = useState({});   // office -> true when folded (Bob 9/28: clicking the box did nothing)
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!user || !user.uid) return undefined;
    const u = subscribeVisibleClaims(db, user, (rows) => setClaims(rows), (e) => { setErr('Claims could not be read: ' + (e.code || e.message)); setClaims([]); });
    return () => { u && u(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && user.uid, user && user.role, user && user.committee, (user && user.scopes || []).join(',')]);

  const self = useMemo(() => tallyClaims(ownClaims(claims || [], user && user.uid)), [claims, user]);
  const matrix = useMemo(() => (mode === 'matrix' ? tallyMatrix(claims || [], seatLabel) : null), [claims, mode]);

  const box = { background: C.panel, border: '1px solid ' + C.line, borderRadius: 8, padding: 12, margin: '12px 0' };
  if (claims === null) return <div style={{ ...box, color: C.dim, fontSize: 12 }}>Counting claims...</div>;
  // Forensic audit 2026-10-16: a failed read used to show the error AND a tally of zeros ("No claims past draft yet."
  // in the matrix) - a count of nothing presented beside the reason there is nothing. Only the failure is shown.
  if (err) {
    return (
      <div style={box} aria-label={mode === 'self' ? 'Your claim tally' : 'Claim tally by office and member'}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{mode === 'self' ? 'Your claims - tally' : 'Claim tally - by office and member'}</div>
        <div style={{ color: C.loss, fontSize: 12 }}>&#9888; {err}. No tally is shown - it would count nothing, not your claims. Reload the page.</div>
      </div>
    );
  }

  if (mode === 'self') {
    return (
      <div style={{ ...box, cursor: onNavigate ? 'pointer' : 'default' }} aria-label="Your claim tally"
        role={onNavigate ? 'button' : undefined} tabIndex={onNavigate ? 0 : undefined}
        onClick={() => onNavigate && onNavigate('claims')} onKeyDown={(e) => { if (onNavigate && e.key === 'Enter') onNavigate('claims'); }}
        title={onNavigate ? 'Open your claims list' : undefined}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 14, marginBottom: 6 }}>Your claims - tally
          {onNavigate ? <span style={{ color: C.dim, fontWeight: 400, fontSize: 11 }}> - click anywhere here to open your claims</span> : null}</div>
        {err ? <div style={{ color: C.loss, fontSize: 12 }}>{err}</div> : null}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr>{HEAD.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody><tr><Cells t={self} /></tr></tbody>
        </table>
        {!reconciles(self) ? <div style={{ color: C.loss, fontSize: 11 }}>The tally does not add up - report this.</div> : null}
        <div style={{ color: C.dim, fontSize: 10, marginTop: 6, lineHeight: 1.5 }}>{FOOT}</div>
      </div>
    );
  }

  const level = user && user.role === 'local_chair' ? 'your local committee(s) of adjustment' : (user && user.committee ? 'the committee ' + user.committee : 'every committee you can see');   // section 528: read "the committee ." for the administrator
  return (
    <div style={box} aria-label="Claim tally by office and member">
      <div style={{ color: C.text, fontWeight: 700, fontSize: 14 }}>Claim tally - by office and member</div>
      <div style={{ color: C.dim, fontSize: 11, marginBottom: 8 }}>Every member in {level}. Click a member's row to open his file; click an office to fold or unfold it.</div>
      {err ? <div style={{ color: C.loss, fontSize: 12 }}>{err}</div> : null}
      {!matrix.offices.length ? <div style={{ color: C.dim, fontSize: 12 }}>No claims past draft yet.</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 620 }}>
            <thead><tr><th style={{ ...th, textAlign: 'left' }}>Office / member</th>{HEAD.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {matrix.offices.map((o) => (
                <React.Fragment key={o.office}>
                  {/* PASS 2 (2026-10-25): the office name folds the row. The <tr> is a row again; the
                      control is in the cell, it carries aria-expanded so assistive tech knows the state,
                      and it says which way it will go in words instead of in a tooltip a phone never shows. */}
                  <tr style={{ background: '#0D1B2A' }}>
                    <td style={{ padding: '8px', borderTop: '1px solid ' + C.line }}>
                      <button type="button"
                        onClick={() => setFolded((f) => ({ ...f, [o.office]: !f[o.office] }))}
                        aria-expanded={!folded[o.office]}
                        style={{ color: C.accent, fontWeight: 700, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', textAlign: 'left' }}>
                        {folded[o.office] ? '\u25B8 ' : '\u25BE '}{o.label} <span style={{ color: C.dim, fontWeight: 400, fontSize: 11 }}>({o.members.length})</span>
                        <span className="sr-only">{folded[o.office] ? ' - show the members' : ' - fold the members away'}</span>
                      </button>
                    </td>
                    <Cells t={o.total} />
                  </tr>
                  {!folded[o.office] && o.members.map((m) => (
                    <tr key={o.office + m.key}
                      style={{ cursor: onOpenMember ? 'pointer' : 'default' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#1B3350'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                      <td style={{ padding: '6px 8px 6px 20px', borderTop: '1px solid ' + C.line }}>
                        {onOpenMember
                          ? <button type="button" onClick={() => onOpenMember(m.key)} aria-label={'Open the member file of ' + m.name}
                              style={{ background: 'none', border: 'none', padding: 0, color: '#7FB3E6', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}>{m.name}</button>
                          : <span style={{ color: C.text }}>{m.name}</span>}
                        {m.joinedBy === 'name' ? <span style={{ color: C.accent, fontSize: 10 }}> (joined by name only)</span> : null}
                      </td>
                      <Cells t={m.tally} />
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              <tr><td style={{ padding: '8px', color: C.text, fontWeight: 700, borderTop: '2px solid ' + C.line }}>All offices</td><Cells t={matrix.grand} /></tr>
            </tbody>
          </table>
        </div>
      )}
      <div style={{ color: C.dim, fontSize: 10, marginTop: 6, lineHeight: 1.5 }}>{FOOT}</div>
    </div>
  );
}
